import { ColumnDef } from '@tanstack/react-table';
import { StudySiteData } from '@/types/study';
import { StatusBadge } from '@/components/ui/StatusBadge';
import { getEntityStatusInfo } from '@/utils/entityStatuses';
import { formatTableDate } from '@/utils/dates';

/** Minimal shape of i18next's `t` — enough for these labels, and it keeps the
 * module free of the i18next generics that make the return type unreadable. */
type Translate = (key: string) => string;

// Shared by the country column's cell renderer and its csvFormatter so the
// two don't drift on how an empty/multi-value list is displayed.
export const formatCountryList = (value: unknown): string => {
  const countries = Array.isArray(value) ? (value as string[]) : [];
  return countries.length ? countries.join(', ') : '-';
};

/** `ColumnManagerPopover` labels, keyed by the same column ids as
 * `buildSiteColumns` so a page can't manage a column under a different name
 * than its header shows. */
export const siteColumnLabels = (t: Translate): Record<string, string> => ({
  display_id: t('sites.fields.id'),
  site_name: t('sites.fields.site_name'),
  protocol_number: t('sites.fields.protocol_number'),
  sponsor_name: t('sites.fields.sponsor_name'),
  country: t('sites.fields.country'),
  pi_name: t('sites.fields.pi_name'),
  submitted_at: t('sites.fields.approved'),
  expiration_date: t('sites.fields.expiration'),
  status: t('sites.fields.status'),
});

/**
 * Every site column definition the app renders, keyed by column id. Each page
 * composes the subset it needs into an array — the Sites index shows all of
 * them, while the Study page's Sites tab drops `protocol_number`/`sponsor_name`
 * (identical on every row within a single study).
 *
 * The `view` action column is deliberately NOT here: the index opens a
 * `SiteDrawer` and the tab has no action column at all, so there is nothing
 * shared to extract.
 */
export const buildSiteColumns = (
  t: Translate
): Record<string, ColumnDef<StudySiteData, unknown>> =>
  // Declared as an array and keyed by `id` on the way out: the
  // `as ColumnDef<StudySiteData, unknown>[]` cast is what lets each cell keep
  // its own typed `getValue()` (the same trick the pages used inline before
  // this module existed), and TypeScript only accepts that widening on an
  // array literal — not on an object literal of the same defs.
  Object.fromEntries(
    (
      [
        {
          header: t('sites.fields.id'),
          id: 'display_id',
          // OV-1375: display_id is now the site's INV number, supplied by the
          // backend for imported and natively-created sites alike. The old
          // `row.id.slice(0, 8)` fallback rendered a chunk of the internal UUID
          // whenever it was absent, which is the "random, unknown ID" this
          // column was reported for — show an em dash instead.
          accessorFn: (row: StudySiteData) => row.display_id || '—',
          enableSorting: true,
          meta: { overflow: 'nowrap', maxWidth: 120, minWidth: 100 },
          cell: ({ getValue }: { getValue: () => string }) => (
            <span className="text-gray-900 font-normal">{getValue()}</span>
          ),
        },
        {
          header: t('sites.fields.site_name'),
          id: 'site_name',
          accessorKey: 'site_name',
          enableSorting: true,
          meta: { overflow: 'truncate', minWidth: 260 },
          cell: ({ getValue }: { getValue: () => string }) => getValue() || '-',
        },
        {
          header: t('sites.fields.protocol_number'),
          id: 'protocol_number',
          accessorFn: (row: StudySiteData) =>
            row.study?.protocol_number ?? null,
          enableSorting: true,
          meta: {
            overflow: 'nowrap',
            maxWidth: 140,
            csvFormatter: value => (value as string) || '-',
          },
          cell: ({ getValue }: { getValue: () => string | null }) => (
            <span className="text-gray-600">{getValue() || '-'}</span>
          ),
        },
        {
          header: t('sites.fields.sponsor_name'),
          id: 'sponsor_name',
          accessorFn: (row: StudySiteData) => row.study?.sponsor_name ?? null,
          enableSorting: true,
          meta: {
            overflow: 'truncate',
            maxWidth: 180,
            csvFormatter: value => (value as string) || '-',
          },
          cell: ({ getValue }: { getValue: () => string | null }) => (
            <span className="text-gray-600">{getValue() || '-'}</span>
          ),
        },
        {
          header: t('sites.fields.country'),
          id: 'country',
          accessorFn: (row: StudySiteData) => row.countries,
          // A site's countries are a string[], so there is no meaningful column
          // sort — the country filter is how you narrow by them.
          enableSorting: false,
          meta: {
            overflow: 'nowrap',
            maxWidth: 100,
            csvFormatter: formatCountryList,
          },
          cell: ({
            getValue,
          }: {
            getValue: () => string[] | null | undefined;
          }) => (
            <span className="text-gray-600">
              {formatCountryList(getValue())}
            </span>
          ),
        },
        {
          header: t('sites.fields.pi_name'),
          id: 'pi_name',
          accessorFn: (row: StudySiteData) => row.pi_name,
          enableSorting: true,
          meta: {
            overflow: 'truncate',
            minWidth: 160,
            csvFormatter: value => (value as string) || '-',
          },
          cell: ({ getValue }: { getValue: () => string | null }) => (
            <span className="text-gray-600">{getValue() || '-'}</span>
          ),
        },
        {
          header: t('sites.fields.approved'),
          id: 'submitted_at',
          accessorFn: (row: StudySiteData) => row.submitted_at,
          enableSorting: true,
          sortingFn: 'nullableDateSort' as const,
          filterFn: 'dateRange' as const,
          meta: {
            overflow: 'nowrap',
            maxWidth: 80,
            csvFormatter: value => formatTableDate(value as string | null),
          },
          cell: ({ getValue }: { getValue: () => string | null }) => (
            <span className="text-gray-600">{formatTableDate(getValue())}</span>
          ),
        },
        {
          header: t('sites.fields.expiration'),
          id: 'expiration_date',
          accessorFn: (row: StudySiteData) => row.expiration_date,
          enableSorting: true,
          sortingFn: 'nullableDateSort' as const,
          filterFn: 'dateRange' as const,
          meta: {
            overflow: 'nowrap',
            maxWidth: 80,
            csvFormatter: value => formatTableDate(value as string | null),
          },
          cell: ({ getValue }: { getValue: () => string | null }) => (
            <span className="text-gray-600">{formatTableDate(getValue())}</span>
          ),
        },
        {
          header: t('sites.fields.status'),
          id: 'status',
          accessorFn: (row: StudySiteData) => row.status,
          enableSorting: true,
          meta: {
            overflow: 'nowrap',
            maxWidth: 100,
            csvFormatter: value =>
              value ? getEntityStatusInfo(value as string).label : '-',
          },
          cell: ({ getValue }: { getValue: () => string | null }) => (
            <StatusBadge status={getValue()} variant="entity" />
          ),
        },
      ] as ColumnDef<StudySiteData, unknown>[]
    ).map(column => [column.id as string, column])
  );
